// The per-option line-item breakdown on the Pricing & Options section. Present only when the
// proposal template turns it on (api-service `proposal_template.show_line_item_breakdown`, e.g.
// "Roof Replacement — Detailed") — the option then carries `line_items`, the same lines the estimate
// editor shows (materials, work & other services, cost roll-up, margin, discount, new total).
// Collapsed by default so a proposal with several options stays scannable.
//
// While the draft is editable the Sales Agent can change a line's price or quantity and add their
// own material / service lines. Those edits are saved on the PROPOSAL (never on the estimate) and the
// server recomputes every total, so what is shown here is always the server's arithmetic.
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type ProposalLineItem = {
  line_id?: string; custom?: boolean;
  name: string; manufacturer?: string | null; product_name?: string | null;
  unit_cost?: number | null; quantity?: number | null; uom?: string | null; amount?: number | null;
};
export type ProposalLineItems = {
  materials: ProposalLineItem[];
  services: ProposalLineItem[];
  cost_rows: { label: string; amount: number }[];
  direct_cost: number;
  discount: { method: string | null; percent: number; amount: number };
  new_total: number;
};
export type DiscountInput = { method: 'PERCENTAGE' | 'FIXED_AMOUNT'; value: number };
export type LineItemInput = { line_id?: string; name: string; unit_cost: number; quantity: number; uom?: string | null };

function fmt(amount: number | null | undefined, currency: string) {
  if (amount == null) return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount); }
  catch { return `$${amount.toFixed(0)}`; }
}
// Quantities are computed floats; three decimals is past anything a roof is measured to.
const qty = (n: number) => String(Number(n.toFixed(3)));
const toInput = (i: ProposalLineItem): LineItemInput => ({
  line_id: i.line_id, name: i.name, unit_cost: i.unit_cost ?? 0, quantity: i.quantity ?? 0, uom: i.uom,
});

const numberField = 'w-16 rounded-md border border-ui-border bg-ui-input px-1 py-0.5 text-right text-xs text-ui-text focus:border-ui-ring focus:outline-none disabled:opacity-60';

/** A number box that only reports a change on blur, and only when it is a real, different value. */
function NumberBox({ value, label, disabled, onCommit }: {
  value: number; label: string; disabled: boolean; onCommit: (v: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input
      type="number" min={0} step="any" inputMode="decimal" aria-label={label} disabled={disabled}
      value={draft ?? String(Number(value.toFixed(3)))}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const parsed = parseFloat(draft ?? '');
        setDraft(null);
        if (!Number.isNaN(parsed) && parsed >= 0 && parsed !== value) onCommit(parsed);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setDraft(null); }}
      className={numberField}
    />
  );
}

/** The option's discount, click-to-edit like the other amounts: click the "−$11", type a dollar
 *  amount ("200") or a percentage ("10%"), Enter to save. The server recomputes the amount (a
 *  percentage follows later cost edits; a fixed one stays put). */
export function DiscountEditor({ discount, currency, busy, onSave, startEditing = false, onDone }: {
  discount: ProposalLineItems['discount']; currency: string; busy: boolean; onSave: (d: DiscountInput) => void;
  /** Opens straight into the input (used by "+ Add discount"). */
  startEditing?: boolean; onDone?: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string | null>(startEditing ? '' : null);
  const isPct = discount.method === 'PERCENTAGE' && discount.percent > 0;
  const commit = () => {
    const raw = (draft ?? '').trim();
    setDraft(null);
    onDone?.();
    const pct = raw.endsWith('%');
    const parsed = parseFloat(pct ? raw.slice(0, -1) : raw.replace(/^\$/, ''));
    if (Number.isNaN(parsed) || parsed < 0) return;
    if (pct ? !(isPct && parsed === discount.percent) : !(!isPct && parsed === discount.amount)) {
      onSave({ method: pct ? 'PERCENTAGE' : 'FIXED_AMOUNT', value: parsed });
    }
  };
  return (
    <div className="flex items-center justify-between gap-2 text-xs text-danger-text">
      <span>
        {t('workflow.estimateDiscount', 'Discount')}
        {isPct ? ` (${qty(discount.percent)}%)` : ''}
      </span>
      {draft !== null ? (
        <input
          autoFocus disabled={busy} value={draft} inputMode="decimal"
          aria-label={t('workflow.proposalDiscountValue', 'Discount: amount, or a percentage like 10%')}
          placeholder="200 or 10%"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setDraft(null); }}
          className={`${numberField} w-24 text-danger-text`}
        />
      ) : (
        <span
          role="button" tabIndex={0}
          title={t('workflow.proposalDiscountClickToEdit', 'Click to edit — an amount, or a percentage like 10%')}
          onClick={() => { if (!busy) setDraft(isPct ? `${qty(discount.percent)}%` : String(discount.amount)); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !busy) setDraft(isPct ? `${qty(discount.percent)}%` : String(discount.amount)); }}
          className="focus-ring cursor-text rounded hover:bg-ui-hover"
        >
          −{fmt(discount.amount, currency)}
        </span>
      )}
    </div>
  );
}

function ItemRow({ item, currency, editable, busy, onChange, onRemove }: {
  item: ProposalLineItem; currency: string; editable: boolean; busy: boolean;
  onChange: (patch: Partial<LineItemInput>) => void; onRemove?: () => void;
}) {
  const { t } = useTranslation();
  const meta = [item.manufacturer, item.product_name].filter(Boolean).join(' · ');
  return (
    <li className="flex items-start justify-between gap-3 border-b border-ui-border2 py-1.5 last:border-b-0">
      <span className="min-w-0">
        <span className="block break-words text-xs font-medium text-ui-text">{item.name}</span>
        {editable ? (
          <span className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-ui-text3">
            <span>$</span>
            <NumberBox value={item.unit_cost ?? 0} label={t('workflow.proposalLinePrice', 'Unit price')} disabled={busy} onCommit={(v) => onChange({ unit_cost: v })} />
            <span>×</span>
            <NumberBox value={item.quantity ?? 0} label={t('workflow.proposalLineQuantity', 'Quantity')} disabled={busy} onCommit={(v) => onChange({ quantity: v })} />
            <span>{item.uom ?? ''}</span>
            {meta && <span className="break-words">· {meta}</span>}
          </span>
        ) : (
          <span className="block break-words text-xs text-ui-text3">
            {[
              item.unit_cost != null && item.quantity != null
                ? `${fmt(item.unit_cost, currency)} × ${qty(item.quantity)} ${item.uom ?? ''}`.trim() : null,
              meta || null,
            ].filter(Boolean).join(' · ')}
          </span>
        )}
      </span>
      <span className="flex flex-shrink-0 items-center gap-2">
        <span className="text-xs font-medium text-ui-text">{fmt(item.amount, currency)}</span>
        {editable && onRemove && (
          <button
            type="button" disabled={busy} onClick={onRemove}
            aria-label={t('workflow.proposalLineRemove', 'Remove this line')}
            className="focus-ring rounded-full px-1.5 text-xs text-ui-text3 hover:bg-ui-hover hover:text-danger-text"
          >×</button>
        )}
      </span>
    </li>
  );
}

function AddLine({ label, busy, onAdd }: { label: string; busy: boolean; onAdd: (line: LineItemInput) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [uom, setUom] = useState('EA');
  const reset = () => { setOpen(false); setName(''); setPrice(''); setQuantity('1'); setUom('EA'); };
  if (!open) {
    return (
      <button
        type="button" onClick={() => setOpen(true)}
        className="focus-ring mt-1 w-full rounded-control border border-dashed border-ui-border px-2 py-1 text-xs font-semibold text-ui-text2 hover:bg-ui-hover"
      >+ {label}</button>
    );
  }
  const valid = name.trim() !== '' && !Number.isNaN(parseFloat(price)) && parseFloat(price) >= 0
    && !Number.isNaN(parseFloat(quantity)) && parseFloat(quantity) > 0;
  const field = 'rounded-md border border-ui-border bg-ui-input px-1.5 py-1 text-xs text-ui-text focus:border-ui-ring focus:outline-none';
  return (
    <div className="mt-1 space-y-1.5 rounded-control border border-ui-border p-2">
      <input
        value={name} onChange={(e) => setName(e.target.value)} autoFocus
        placeholder={t('workflow.proposalLineName', 'Name')} aria-label={t('workflow.proposalLineName', 'Name')}
        className={`${field} w-full`}
      />
      <div className="flex flex-wrap items-center gap-1.5">
        <input value={price} onChange={(e) => setPrice(e.target.value)} type="number" min={0} step="any" inputMode="decimal"
          placeholder="$" aria-label={t('workflow.proposalLinePrice', 'Unit price')} className={`${field} w-20`} />
        <span className="text-xs text-ui-text3">×</span>
        <input value={quantity} onChange={(e) => setQuantity(e.target.value)} type="number" min={0} step="any" inputMode="decimal"
          aria-label={t('workflow.proposalLineQuantity', 'Quantity')} className={`${field} w-16`} />
        <input value={uom} onChange={(e) => setUom(e.target.value)} aria-label={t('workflow.proposalLineUom', 'Unit')} className={`${field} w-16`} />
      </div>
      <div className="flex gap-1.5">
        <button
          type="button" disabled={!valid || busy}
          onClick={() => { onAdd({ name: name.trim(), unit_cost: parseFloat(price), quantity: parseFloat(quantity), uom: uom.trim() || null }); reset(); }}
          className="focus-ring rounded-full bg-accent-fill px-3 py-0.5 text-xs font-semibold text-on-accent disabled:opacity-50"
        >{t('workflow.proposalLineAdd', 'Add')}</button>
        <button type="button" onClick={reset} className="focus-ring rounded-full px-3 py-0.5 text-xs font-semibold text-ui-text2 hover:bg-ui-hover">
          {t('workflow.cancel', 'Cancel')}
        </button>
      </div>
    </div>
  );
}

function Group({ title, addLabel, items, currency, editable, busy, onChange }: {
  title: string; addLabel: string; items: ProposalLineItem[]; currency: string; editable: boolean; busy: boolean;
  onChange: (next: LineItemInput[]) => void;
}) {
  if (items.length === 0 && !editable) return null;
  const all = items.map(toInput);
  return (
    <div>
      <p className="text-micro font-bold uppercase tracking-[0.06em] text-ui-text3">{title} ({items.length})</p>
      <ul>
        {items.map((item, i) => (
          <ItemRow
            key={item.line_id ?? `${item.name}-${i}`} item={item} currency={currency} editable={editable} busy={busy}
            onChange={(patch) => onChange(all.map((l, j) => (j === i ? { ...l, ...patch } : l)))}
            onRemove={() => onChange(all.filter((_, j) => j !== i))}
          />
        ))}
      </ul>
      {editable && <AddLine label={addLabel} busy={busy} onAdd={(line) => onChange([...all, line])} />}
    </div>
  );
}

export default function ProposalLineItemsBreakdown({
  items, currency, editable = false, busy = false, onSave, onDiscountSave,
}: {
  items: ProposalLineItems; currency: string;
  /** Draft is editable and the pricing section allows edits. */
  editable?: boolean; busy?: boolean;
  onSave?: (materials: LineItemInput[], services: LineItemInput[]) => void;
  onDiscountSave?: (d: DiscountInput) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [addingDiscount, setAddingDiscount] = useState(false);
  const panelId = useId();
  const count = items.materials.length + items.services.length;
  const canEdit = editable && !!onSave;
  if (count === 0 && !canEdit) return null;
  const mats = items.materials.map(toInput);
  const svcs = items.services.map(toInput);
  return (
    <div className="mt-1.5">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="focus-ring rounded-full px-2 py-0.5 text-xs font-semibold text-accent-text hover:bg-ui-hover"
      >
        {open
          ? t('workflow.proposalHideLineItems', 'Hide line items')
          : t('workflow.proposalShowLineItems', 'Show line items ({{count}})', { count })}
      </button>
      {open && (
        <div id={panelId} className="mt-1.5 space-y-2.5 rounded-control border border-ui-border2 bg-ui-card px-2.5 py-2">
          <Group
            title={t('workflow.proposalLineItemsMaterials', 'Materials')} addLabel={t('workflow.proposalAddMaterial', 'Add material')}
            items={items.materials} currency={currency} editable={canEdit} busy={busy}
            onChange={(next) => onSave?.(next, svcs)}
          />
          <Group
            title={t('workflow.proposalLineItemsServices', 'Work & other services')} addLabel={t('workflow.proposalAddService', 'Add service')}
            items={items.services} currency={currency} editable={canEdit} busy={busy}
            onChange={(next) => onSave?.(mats, next)}
          />
          <div className="space-y-0.5 border-t border-ui-border pt-2 text-xs">
            {items.cost_rows.map((row) => (
              <div key={row.label} className="flex justify-between text-ui-text2">
                <span>{row.label}</span><span>{fmt(row.amount, currency)}</span>
              </div>
            ))}
            <div className="flex justify-between text-ui-text2">
              <span>{t('workflow.proposalLineItemsDirectCost', 'Subtotal')}</span>
              <span>{fmt(items.direct_cost, currency)}</span>
            </div>
            {items.discount.amount > 0 && (
              <div className="flex justify-between text-danger-text">
                <span>
                  {t('workflow.estimateDiscount', 'Discount')}
                  {items.discount.percent > 0 ? ` (${qty(items.discount.percent)}%)` : ''}
                </span>
                <span>−{fmt(items.discount.amount, currency)}</span>
              </div>
            )}
            {canEdit && onDiscountSave && items.discount.amount <= 0 && (addingDiscount ? (
              <DiscountEditor
                discount={items.discount} currency={currency} busy={busy} startEditing
                onSave={onDiscountSave} onDone={() => setAddingDiscount(false)}
              />
            ) : (
              <button
                type="button" disabled={busy} onClick={() => setAddingDiscount(true)}
                className="focus-ring rounded-full px-2 py-0.5 text-xs font-semibold text-accent-text hover:bg-ui-hover"
              >+ {t('workflow.proposalAddDiscount', 'Add discount')}</button>
            ))}
            <div className="flex justify-between border-t border-ui-border pt-1.5 text-sm font-semibold text-ui-text">
              <span>{t('estimates.editor.newTotal', 'New total')}</span>
              <span>{fmt(items.new_total, currency)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
